import { UploadOutlined } from '@ant-design/icons';
import { Col, Row, Upload } from 'antd';
import { RcFile, UploadChangeParam } from 'antd/lib/upload';
import { isEmpty } from 'lodash';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import { MButton, MFormRadioGroup, MUploadFile } from 'src/lib';
import { Spacing } from 'src/styles';

import { MFormItemError } from '../MFormItemError/MFormItemError';
import { MFormItemLabel } from '../MFormItemLabel/MFormItemLabel';

import * as Styles from './MUpload.styles';

interface MUploadProps {
  label?: string;
  accept: string[];
  value?: string;
  defaultValue?: string;
  options?: { value: string; label: string }[];
  error?: string;
  isLoading?: boolean;
  fileList?: { name: string; uid: string }[];
  percent?: number;
  onChange?: (value: string) => void;
  beforeUpload?: (file: RcFile) => Promise<boolean | void | typeof Upload.LIST_IGNORE>;
  onUploadChange?: (info: Pick<UploadChangeParam, 'file'>) => void;
  onUpload: (param: Pick<UploadRequestOption, 'file' | 'onSuccess' | 'onError'>) => void;
  uploadedFileList?: { id: string; name: string; type?: string }[];
  onCancelUploadedFile?: (id: string) => void;
  onDeleteUploadedFile?: (id: string) => void;
  deleteUploadedFileLabel?: string;
  testId?: string;
}

export const MUpload = ({
  label,
  accept,
  value,
  defaultValue,
  options,
  error,
  isLoading,
  fileList,
  percent,
  onChange,
  beforeUpload,
  onUploadChange,
  onUpload,
  uploadedFileList = [],
  onCancelUploadedFile,
  onDeleteUploadedFile,
  deleteUploadedFileLabel,
  testId,
}: MUploadProps) => {
  const hasMultipleTypes = () => options && options.length > 1;

  const _onUpload = async ({ file, onSuccess, onError }: UploadRequestOption) => {
    onUpload?.({ file, onSuccess, onError });
  };

  if (!isEmpty(uploadedFileList)) {
    return (
      <Col span={24}>
        {uploadedFileList.map(file => (
          <Row key={file.id}>
            {file.type && (
              <Col span={24}>
                <MFormItemLabel value={file.type} weight='light' testId={testId} />
              </Col>
            )}
            <Col span={24} key={file.id} className={Spacing.mb12}>
              <MUploadFile
                id={file.id}
                name={file.name}
                onCancel={onCancelUploadedFile}
                onDelete={onDeleteUploadedFile}
                deleteLabel={deleteUploadedFileLabel}
                testId={testId}
              />
            </Col>
          </Row>
        ))}
      </Col>
    );
  }

  return (
    <Col span={24}>
      <Row>
        {hasMultipleTypes() && label && (
          <Col span={24}>
            <MFormRadioGroup
              label={label}
              defaultValue={defaultValue}
              value={value}
              options={options ?? []}
              onChange={value => {
                onChange?.(value);
              }}
              error={error}
              align='vertical'
              className={Spacing.mb0}
              testId={testId}
            />
          </Col>
        )}
        {!hasMultipleTypes() && (
          <Col span={24}>
            <MFormItemError value={error} testId={testId + '-error-message'} />
          </Col>
        )}
        <Col span={24} className={Spacing.mb12}>
          <Upload
            accept={accept.join(',')}
            maxCount={1}
            name='file'
            multiple={false}
            showUploadList={false}
            disabled={isLoading || !value}
            customRequest={_onUpload}
            beforeUpload={async (file: RcFile) => {
              return beforeUpload?.(file);
            }}
            onChange={info => {
              onUploadChange?.(info);
            }}
            className={Styles.upload}
            data-testid={`${testId}-input-file`}>
            <MButton type='secondary' disabled={isLoading || !value} testId={`${testId}-upload-btn`}>
              <UploadOutlined style={{ height: 16, width: 16 }} /> Upload
            </MButton>
          </Upload>
        </Col>
        <Col span={24}>
          {fileList?.map(file => (
            <MUploadFile
              key={file.uid}
              id={file.uid}
              name={file.name}
              danger={Boolean(error) && !isLoading}
              percent={percent}
              testId={testId}
            />
          ))}
        </Col>
      </Row>
    </Col>
  );
};
